import { useCallback, useEffect, useState } from "react";

export type Tema = "claro" | "escuro";

const CHAVE_ARMAZENADA = "portal.tema";
const CONSULTA_ESCURO = "(prefers-color-scheme: dark)";

function ehTema(valor: unknown): valor is Tema {
  return valor === "claro" || valor === "escuro";
}

/** A escolha explícita desta pessoa, ou `null` enquanto ela não clicou no botão.
 *  O antigo valor "sistema" (a opção saiu em 08/10/2026) também vale como sem escolha. */
function lerEscolhaGuardada(): Tema | null {
  // O acesso ao localStorage lança em janela anônima e com dados de site bloqueados.
  // Nesse caso o portal segue a preferência do sistema até a pessoa clicar.
  try {
    const guardado = window.localStorage.getItem(CHAVE_ARMAZENADA);
    return ehTema(guardado) ? guardado : null;
  } catch {
    return null;
  }
}

function consultaDoSistema(): MediaQueryList | null {
  return typeof window.matchMedia === "function" ? window.matchMedia(CONSULTA_ESCURO) : null;
}

function temaDoSistema(): Tema {
  return consultaDoSistema()?.matches ? "escuro" : "claro";
}

/** Tema desta pessoa neste navegador: só claro ou escuro. Sem escolha guardada, o documento
 *  fica sem marca e o CSS segue o sistema; o hook devolve o tema que está de fato na tela. */
export function useTema(): { tema: Tema; alternarTema: () => void } {
  const [escolha, setEscolha] = useState<Tema | null>(lerEscolhaGuardada);
  const [doSistema, setDoSistema] = useState<Tema>(temaDoSistema);

  useEffect(() => {
    const raiz = document.documentElement;
    if (escolha === null) {
      raiz.removeAttribute("data-theme");
      return;
    }
    raiz.setAttribute("data-theme", escolha === "escuro" ? "dark" : "light");
  }, [escolha]);

  useEffect(() => {
    const consulta = consultaDoSistema();
    if (!consulta) return;
    const aoMudar = () => setDoSistema(consulta.matches ? "escuro" : "claro");
    consulta.addEventListener("change", aoMudar);
    return () => consulta.removeEventListener("change", aoMudar);
  }, []);

  const tema = escolha ?? doSistema;

  const alternarTema = useCallback(() => {
    const novo: Tema = tema === "escuro" ? "claro" : "escuro";
    setEscolha(novo);
    try {
      window.localStorage.setItem(CHAVE_ARMAZENADA, novo);
    } catch {
      // Preferência não persistida é um incômodo, não um erro: a sessão atual já mudou.
    }
  }, [tema]);

  return { tema, alternarTema };
}
